/**
 * 게시글 작성/수정 폼 컴포넌트
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { boardService, BoardFormData } from '../../services/boardService';
import { Button } from '../../../../shared/components/UI/Button';
import { Input } from '../../../../shared/components/UI/Input';
import { Card } from '../../../../shared/components/UI/Card';
import { BoardFile } from '../../../../shared/types/board.types';
import { invalidateBoardCache } from '../../../../shared/utils/cacheInvalidation';
import styles from './BoardForm.module.css';

export const BoardForm: React.FC = () => {
  const navigate = useNavigate();
  const { boardType, id } = useParams<{ boardType: string; id?: string }>();
  const isEdit = !!id;

  const [formData, setFormData] = useState({
    title: '',
    content: '',
  });
  const [existingFiles, setExistingFiles] = useState<BoardFile[]>([]);
  const [newFiles, setNewFiles] = useState<File[]>([]);
  const [deleteFileIds, setDeleteFileIds] = useState<number[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isEdit && id && boardType) {
      loadPost();
    }
  }, [isEdit, id, boardType]);

  const loadPost = async () => {
    try {
      const post = await boardService.getPost(boardType!, parseInt(id!));
      setFormData({
        title: post.title,
        content: post.content,
      });
      setExistingFiles(post.files || []);
    } catch (error: any) {
      console.error('Failed to load post:', error);
      // 401 에러는 인터셉터에서 처리하므로 alert 표시하지 않음
      if (error?.response?.status !== 401) {
        alert('게시글을 불러오는데 실패했습니다.');
      }
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    setNewFiles([...newFiles, ...files]);
  };

  const handleRemoveNewFile = (index: number) => {
    setNewFiles(newFiles.filter((_, i) => i !== index));
  };

  const handleRemoveExistingFile = (fileId: number) => {
    setDeleteFileIds([...deleteFileIds, fileId]);
    setExistingFiles(existingFiles.filter(f => f.id !== fileId));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!boardType) return;

    try {
      setLoading(true);

      const data: BoardFormData = {
        title: formData.title,
        content: formData.content,
      };

      if (isEdit && id) {
        data.deleteFileIds = deleteFileIds;
        await boardService.updatePost(boardType, parseInt(id), data, newFiles);
        alert('수정되었습니다.');
      } else {
        await boardService.createPost(boardType, data, newFiles);
        alert('등록되었습니다.');
      }

      // 캐시 무효화 (사용자 페이지의 게시판 데이터 갱신)
      invalidateBoardCache(boardType);

      // 해당 게시판 관리 페이지로 이동
      navigate(-1); // 이전 페이지(게시판 목록)로 돌아가기
    } catch (error: any) {
      console.error('Failed to save post:', error);
      // 401 에러는 인터셉터에서 처리하므로 alert 표시하지 않음
      if (error?.response?.status !== 401) {
        alert('저장에 실패했습니다.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.boardForm}>
      <h1>{isEdit ? '게시글 수정' : '새 게시글 작성'}</h1>

      <Card>
        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.formGroup}>
            <label>제목 *</label>
            <Input
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="제목을 입력하세요"
              required
            />
          </div>

          <div className={styles.formGroup}>
            <label>내용</label>
            <textarea
              value={formData.content}
              onChange={(e) => setFormData({ ...formData, content: e.target.value })}
              placeholder="내용을 입력하세요"
              className={styles.textarea}
              rows={10}
            />
          </div>

          <div className={styles.formGroup}>
            <label>파일 첨부</label>
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={handleFileSelect}
            />

            {/* 기존 파일 목록 */}
            {existingFiles.length > 0 && (
              <div className={styles.fileSection}>
                <h4>기존 파일</h4>
                <div className={styles.imageList}>
                  {existingFiles.map((file) => (
                    <div key={file.id} className={styles.imageItem}>
                      <img src={file.fileUrl} alt={file.originalFileName} />
                      <p>{file.originalFileName}</p>
                      <button
                        type="button"
                        onClick={() => handleRemoveExistingFile(file.id)}
                        className={styles.removeButton}
                      >
                        삭제
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* 새로 추가된 파일 목록 */}
            {newFiles.length > 0 && (
              <div className={styles.fileSection}>
                <h4>새로 추가된 파일</h4>
                <div className={styles.imageList}>
                  {newFiles.map((file, index) => (
                    <div key={index} className={styles.imageItem}>
                      <img src={URL.createObjectURL(file)} alt={file.name} />
                      <p>{file.name}</p>
                      <button
                        type="button"
                        onClick={() => handleRemoveNewFile(index)}
                        className={styles.removeButton}
                      >
                        삭제
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className={styles.formActions}>
            <Button type="submit" disabled={loading}>
              {loading ? '저장 중...' : '저장'}
            </Button>
            <Button type="button" onClick={() => navigate('/admin/board')}>
              취소
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
};
